import authentication from '@feathersjs/authentication-client'
import * as errors from '@feathersjs/errors'
import { feathers } from '@feathersjs/feathers'
import rest, { FetchClient } from '@feathersjs/rest-client'
import { i18n } from 'boot/i18n'
import { createPiniaClient } from 'feathers-pinia'
import { jwtDecode } from 'jwt-decode'
import { Notify } from 'quasar'
import { pinia } from 'src/modules/pinia'
import { getAPIBaseURL } from 'src/utils/apiBaseUrl'
import { skipListStore } from 'src/utils/serviceList'
import { skipWebhookStore } from 'src/utils/webhookStore'

const accessTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-jwt' : 'vision-customer-jwt'
const refreshTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-refresh-jwt' : 'vision-customer-refresh-jwt'
const loginRedirectStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-login-redirect' : 'vision-customer-login-redirect'

let refreshInFlight = null
let isPageTearingDown = false
let updateRateLimitToast = null
let rateLimitToastUntil = 0
let rateLimitToastMessage = ''
let rateLimitToastInterval = null
let rateLimitToastDuration = 0

const rateLimitErrorCodes = new Set(['RATE_LIMIT_EXCEEDED', 'AUTHENTICATION_RATE_LIMIT_EXCEEDED'])

function isNetworkError(err) {
  return err instanceof TypeError
}

function isAbortError(err) {
  return err?.name === 'AbortError'
}

function shouldSkipLogoutForTransientError() {
  return isPageTearingDown
}

function isSharingCenterMeetingPath() {
  if (process.env.FRONTEND_TYPE !== 'Customer' || typeof window === 'undefined') {
    return false
  }

  return /^\/meeting\/[^/]+/.test(String(window.location.pathname || ''))
}

function clearRateLimitToastInterval() {
  if (rateLimitToastInterval) {
    window.clearInterval(rateLimitToastInterval)
    rateLimitToastInterval = null
  }
}

function getRateLimitRetryAfterSeconds(error) {
  const rawRetryAfterSeconds =
    error?.errors?.retryAfterSeconds ?? error?.retryAfterSeconds ?? error?.data?.retryAfterSeconds ?? error?.response?.headers?.get?.('Retry-After')

  const retryAfterSeconds = Number(rawRetryAfterSeconds)
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? Math.ceil(retryAfterSeconds) : null
}

function escapeHtml(value) {
  return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;')
}

function getRateLimitRetryTimeText(remainingSeconds) {
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60
  const timeParts = []

  if (minutes > 0) {
    timeParts.push(`${minutes} ${i18n.global.t('Shared.Globals.Minutes', minutes).toLowerCase()}`)
  }

  if (seconds > 0 || minutes === 0) {
    timeParts.push(`${seconds} ${i18n.global.t('Shared.Globals.Seconds', seconds).toLowerCase()}`)
  }

  return timeParts.join(' ')
}

function getRateLimitToastCaption(remainingMs) {
  const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000))
  const progressPercent = rateLimitToastDuration > 0 ? Math.max(0, Math.min(100, (remainingMs / rateLimitToastDuration) * 100)) : 0
  const countdownText = escapeHtml(i18n.global.t('Shared.Globals.Rate limit retry', { time: getRateLimitRetryTimeText(remainingSeconds) }))

  return `
    <div>${countdownText}</div>
    <div style="margin-top: 8px; height: 4px; border-radius: 999px; overflow: hidden; background: rgba(255, 255, 255, 0.28);">
      <div style="height: 100%; width: ${progressPercent}%; background: rgba(255, 255, 255, 0.92); transition: width 1s linear;"></div>
    </div>
  `
}

function dismissRateLimitToast() {
  clearRateLimitToastInterval()
  rateLimitToastUntil = 0
  rateLimitToastMessage = ''
  rateLimitToastDuration = 0

  if (updateRateLimitToast) {
    updateRateLimitToast()
    updateRateLimitToast = null
  }
}

function updateRateLimitToastState() {
  if (!updateRateLimitToast || !rateLimitToastUntil) {
    return
  }

  const remainingMs = rateLimitToastUntil - Date.now()
  if (remainingMs <= 0) {
    dismissRateLimitToast()
    return
  }

  updateRateLimitToast({
    html: true,
    message: escapeHtml(rateLimitToastMessage),
    caption: getRateLimitToastCaption(remainingMs),
    timeout: 0,
    progress: false,
    multiLine: true,
  })
}

function showRateLimitToast(error) {
  const errorCode = error?.errors?.errorCode

  if (!rateLimitErrorCodes.has(errorCode)) {
    return
  }

  const retryAfterSeconds = getRateLimitRetryAfterSeconds(error)
  if (!retryAfterSeconds) {
    return
  }

  const nextToastUntil = Date.now() + retryAfterSeconds * 1000

  if (updateRateLimitToast && nextToastUntil <= rateLimitToastUntil) {
    return
  }

  rateLimitToastMessage = i18n.global.t('Shared.Globals.Rate limit exceeded')
  rateLimitToastUntil = nextToastUntil
  rateLimitToastDuration = retryAfterSeconds * 1000

  const notifyOptions = {
    group: false,
    type: 'negative',
    icon: 'sym_o_timer',
    position: 'top',
    html: true,
    message: escapeHtml(rateLimitToastMessage),
    caption: getRateLimitToastCaption(rateLimitToastDuration),
    timeout: 0,
    progress: false,
    multiLine: true,
  }

  if (updateRateLimitToast) {
    updateRateLimitToast(notifyOptions)
    clearRateLimitToastInterval()
    rateLimitToastInterval = window.setInterval(updateRateLimitToastState, 1000)
    return
  }

  let dismissOrUpdate
  dismissOrUpdate = Notify.create({
    ...notifyOptions,
    onDismiss: () => {
      if (updateRateLimitToast === dismissOrUpdate) {
        updateRateLimitToast = null
        clearRateLimitToastInterval()
        rateLimitToastUntil = 0
        rateLimitToastMessage = ''
      }
    },
  })

  updateRateLimitToast = dismissOrUpdate
  clearRateLimitToastInterval()
  rateLimitToastInterval = window.setInterval(updateRateLimitToastState, 1000)
}

const markPageTearingDown = () => {
  isPageTearingDown = true
}

window.addEventListener('beforeunload', markPageTearingDown, { capture: true })
window.addEventListener('pagehide', markPageTearingDown, { capture: true })

function saveLoginRedirect() {
  if (isSharingCenterMeetingPath()) {
    return
  }

  if (window.location.pathname.startsWith('/auth')) {
    return
  }

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
  window.localStorage.setItem(loginRedirectStorageKey, currentPath)
}

async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refreshToken = window.localStorage.getItem(refreshTokenStorageKey)
    if (!refreshToken) throw new Error('No refresh token available')

    try {
      const result = await feathersClient.service('authentication').create({
        strategy: 'refresh',
        refreshToken,
      })

      if (!result?.accessToken) throw new Error('Refresh did not return accessToken')

      window.localStorage.setItem(accessTokenStorageKey, result.accessToken)

      if (result.refreshToken) {
        window.localStorage.setItem(refreshTokenStorageKey, result.refreshToken)
      }

      await feathersClient.authentication.setAccessToken(result.accessToken)

      return result.accessToken
    } catch (e) {
      await forceLogout()
      throw e
    }
  })()

  try {
    return await refreshInFlight
  } finally {
    refreshInFlight = null
  }
}

async function forceLogout() {
  try {
    await feathersClient.authentication.removeAccessToken()
  } catch (_) {
    /* empty */
  }

  window.localStorage.removeItem(accessTokenStorageKey)
  window.localStorage.removeItem(refreshTokenStorageKey)

  if (isSharingCenterMeetingPath()) {
    return
  }

  saveLoginRedirect()
  window.location.href = '/auth/login'
}

class CustomFetch extends FetchClient {
  checkStatus(response) {
    if (response.ok) {
      return response
    }

    const ErrorClass = errors[response.status] || Error

    // Try to parse JSON error body, but ALWAYS keep the HTTP status code.
    return response
      .json()
      .catch(() => {
        const err = new ErrorClass('Non-JSON error response')
        err.code = response.status
        err.name = ErrorClass.name || 'HTTPError'
        err.response = response
        return err
      })
      .then((error) => {
        // If we got a parsed Feathers error, it likely already has `code`,
        // but ensure it’s present.
        if (!error.code) error.code = response.status
        error.response = response
        throw error
      })
  }

  async request(options, params) {
    const fetchOptions = Object.assign({}, options, params.connection)
    fetchOptions.credentials = fetchOptions.credentials || 'include'
    const skipAuthHeader = params?.skipAuthHeader === true
    const skipAuthRefresh = params?.skipAuthRefresh === true
    const skipAuthRedirect = params?.skipAuthRedirect === true || isSharingCenterMeetingPath()

    fetchOptions.headers = Object.assign(
      {
        Accept: 'application/json',
      },
      this.options.headers,
      fetchOptions.headers,
    )

    if (skipAuthHeader) {
      const authorizationHeaderName = Object.keys(fetchOptions.headers).find((headerName) => String(headerName || '').toLowerCase() === 'authorization')
      if (authorizationHeaderName) {
        delete fetchOptions.headers[authorizationHeaderName]
      }
    }

    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    const doFetch = async () => {
      const response = await this.connection(options.url, fetchOptions).then(this.checkStatus)
      dismissRateLimitToast()

      if (response.status === 204) {
        return null
      }

      const contentType = response.headers.get('Content-Type')
      if (contentType && contentType.includes('application/json')) {
        return response.json()
      }
      return response
    }

    try {
      return await doFetch()
    } catch (error) {
      if (isNetworkError(error) || isAbortError(error)) {
        if (shouldSkipLogoutForTransientError() || skipAuthRedirect) {
          throw error
        }
        await forceLogout()
        throw error
      }

      const code = error?.code ?? error?.response?.status
      const errorCode = error?.errors?.errorCode
      // MFA redirect logic stays as-is
      if (code === 403 && error?.errors?.errorCode === 'MFA_EXPIRED') {
        window.location.href = '/auth/verify-mfa'
        throw error
      }

      // Pending MFA session expired should be handled by the MFA screen itself
      // (showing a user-friendly toast), not by global auto-refresh/logout.
      if (code === 401 && errorCode === 'MFA_SESSION_EXPIRED') {
        throw error
      }

      if (code === 429 && rateLimitErrorCodes.has(errorCode)) {
        showRateLimitToast(error)
      }

      // Avoid infinite loops:
      // - don't refresh while calling /authentication
      // - retry only once
      const url = String(options.url || '')
      const isAuthCall = url.includes('/authentication')
      const alreadyRetried = options && options.__retried === true
      const strategy = options?.body?.strategy
      const skipLogoutRetry = params?.skipLogoutRetry === true

      // If the strategy is the logon request with user/pw, do not try a refresh.
      // Propagate the original auth error so the login form can render it.
      if (strategy === 'local') {
        throw error
      }

      if (skipLogoutRetry) {
        throw error
      }

      if (isAuthCall && strategy === 'refresh' && (code === 401 || code === 403)) {
        // If the refresh call itself fails -> do NOT try to refresh again (deadlock)
        if (!skipAuthRedirect) {
          await forceLogout()
        }
        throw error
      }

      if (code === 401 && skipAuthRefresh) {
        throw error
      }

      if (code === 401 && !alreadyRetried) {
        try {
          const newAccessToken = await refreshAccessToken()
          const latestRefreshToken = String(window.localStorage.getItem(refreshTokenStorageKey) || '')
          // Mark so we only retry once
          options.__retried = true

          if (!isAuthCall) {
            if (fetchOptions.headers) {
              // Remove any stale Authorization header; feathers auth will re-add based on the new token
              fetchOptions.headers['Authorization'] = `Bearer ${newAccessToken}`

              const refreshHeaderName = Object.keys(fetchOptions.headers).find((headerName) => String(headerName || '').toLowerCase() === 'x-refresh-token')
              if (refreshHeaderName) {
                if (latestRefreshToken) {
                  fetchOptions.headers[refreshHeaderName] = latestRefreshToken
                } else {
                  delete fetchOptions.headers[refreshHeaderName]
                }
              }
            }

            if (options?.body && typeof options.body === 'object' && Object.prototype.hasOwnProperty.call(options.body, 'refreshToken')) {
              if (latestRefreshToken) {
                options.body.refreshToken = latestRefreshToken
              } else {
                delete options.body.refreshToken
              }

              fetchOptions.body = JSON.stringify(options.body)
            }
          } else {
            if (options?.body?.strategy === 'jwt') {
              fetchOptions.body = JSON.stringify({
                strategy: 'jwt',
                accessToken: newAccessToken,
              })
            }
          }

          return await doFetch()
        } catch (refreshErr) {
          if (!skipAuthRedirect) {
            await forceLogout()
          }
          throw error
        }
      }

      // If it's a 401 and we didn't refresh (auth call / already retried),
      // behave like before: clear and redirect (but avoid reload loops on "Invalid login")
      if (code === 401) {
        await feathersClient.authentication.removeAccessToken()
        window.localStorage.removeItem(refreshTokenStorageKey)

        if (error?.message !== 'Invalid login' && !skipAuthRedirect) {
          saveLoginRedirect()
          window.location.href = '/auth/login'
        }
      }

      throw error
    }
  }
}

export function consumeAuthTokensFromHash() {
  const rawHash = String(window.location.hash || '')
  if (!rawHash) {
    return false
  }

  const hash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
  const params = new URLSearchParams(hash)
  const access = params.get('access_token')
  const refresh = params.get('refresh_token')
  const hasAuthTokensInHash = !!(access || refresh)
  if (!hasAuthTokensInHash) {
    return false
  }

  try {
    if (access) {
      window.localStorage.setItem(accessTokenStorageKey, access)

      let decodedAccessToken = null
      try {
        decodedAccessToken = jwtDecode(access)
      } catch (_) {
        decodedAccessToken = null
      }

      if (decodedAccessToken?.customer?._id) {
        window.localStorage.setItem('selectedCustomer', decodedAccessToken.customer._id)
      }
      if (decodedAccessToken?.identifier) {
        window.localStorage.setItem('totpIdentifier', decodedAccessToken.identifier)
        window.localStorage.setItem('rememberMFA', 'false')
      }

      if (!refresh) {
        window.localStorage.removeItem(refreshTokenStorageKey)
      }
    }

    if (refresh) {
      window.localStorage.setItem(refreshTokenStorageKey, refresh)
    }

    return true
  } finally {
    params.delete('access_token')
    params.delete('refresh_token')
    const remainingHash = params.toString()
    const cleanURL = `${window.location.pathname}${window.location.search}${remainingHash ? `#${remainingHash}` : ''}`
    let shouldForceReplace = false

    try {
      window.history.replaceState({}, document.title, cleanURL)
    } catch (_) {
      shouldForceReplace = true
    }

    if (!shouldForceReplace) {
      shouldForceReplace = window.location.hash.includes('access_token=') || window.location.hash.includes('refresh_token=')
    }

    if (shouldForceReplace) {
      window.location.replace(cleanURL)
    }
  }
}

consumeAuthTokensFromHash()

const apiBaseUrl = getAPIBaseURL()

const restClient = rest(apiBaseUrl).fetch(window.fetch.bind(window), CustomFetch)

export const feathersClient = feathers()
  .configure(restClient)
  .configure(
    authentication({
      storage: window.localStorage,
      storageKey: accessTokenStorageKey,
    }),
  )
  .use('users', restClient.service('users'), {
    methods: [
      'find',
      'get',
      'create',
      'patch',
      'remove',
      'activateMFA',
      'sendMFASetupCode',
      'verifyMFASetupCode',
      'sendMFACode',
      'verifyMFA',
      'resetMFA',
      'resendInvite',
      'switchCustomer',
      'verifyToken',
      'impersonate',
      'filters',
      'findApiAccounts',
      'getApiAccount',
      'createApiAccount',
      'patchApiAccount',
      'removeApiAccount',
      'regenerateApiToken',
      'findApiRoles',
    ],
  })
  .use('user-refresh-tokens', restClient.service('user-refresh-tokens'), {
    methods: ['find', 'get', 'remove'],
  })
  .use('authmanagement-roles', restClient.service('authmanagement-roles'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'listPermissions', 'bulkAssignCustomerRoles'],
  })
  .use('whitelabels', restClient.service('whitelabels'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'resolve', 'filters'],
  })
  .use('licenses', restClient.service('licenses'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'findUsers', 'findMicrosoftLicenses', 'summary'],
  })
  .use('license-partner-programs', restClient.service('license-partner-programs'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('license-slas', restClient.service('license-slas'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('webhosting', restClient.service('webhosting'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'autologin', 'sendAccountDetails', 'upgradeHosting', 'createFull', 'filters'],
  })
  .use('webhosting-packages', restClient.service('webhosting-packages'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('contracts', restClient.service('contracts'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'orderSubscriptions', 'filters'],
  })
  .use('overview-totals', restClient.service('overview-totals'), { methods: ['find'] })
  .use('microsoft-privileged-sign-ins', restClient.service('microsoft-privileged-sign-ins'), { methods: ['find', 'overview', 'filters'] })
  .use('customers', restClient.service('customers'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'export', 'assets', 'assetOverview', 'assetHistory', 'assetDetails'],
  })
  .use('customer-assets', restClient.service('customer-assets'), {
    methods: ['findBackups'],
  })
  .use('customer-contacts', restClient.service('customer-contacts'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'unsubscribeMail', 'filters', 'export'],
  })
  .use('account-invitations', restClient.service('account-invitations'), {
    methods: [
      'find',
      'get',
      'filters',
      'mailchimpStatus',
      'previewMailchimp',
      'prepareMailchimp',
      'export',
      'resolve',
      'accept',
      'revoke',
      'retryMailchimp',
      'reissue',
    ],
  })
  .use('customer-offers', restClient.service('customer-offers'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'acceptOffer', 'createOfferPDF', 'filters'],
  })
  .use('customer-users', restClient.service('customer-users'), {
    methods: [
      'find',
      'get',
      'patch',
      'remove',
      'createNew',
      'createApiAccount',
      'filters',
      'canPatchUser',
      'findUsers',
      'findRoles',
      'findCustomers',
      'patchCustomerUser',
      'multiAssignRoles',
      'regenerateApiToken',
      'resetMFA',
      'resendInvite',
    ],
  })
  .use('sales-orders', restClient.service('sales-orders'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'resumeStockDelivery'],
  })
  .use('sharingcenter-colleagues', restClient.service('sharingcenter-colleagues'), {
    methods: ['find', 'callColleague', 'acceptCall', 'declineCall', 'cancelCall', 'setPresenceStatus'],
  })
  .use('sharingcenter-appointment-invite', restClient.service('sharingcenter-appointment-invite'), {
    methods: ['find', 'invite', 'accept', 'decline'],
  })
  .use('ssl-certificates', restClient.service('ssl-certificates'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('audit-logs', restClient.service('audit-logs'), {
    methods: ['find', 'get', 'filters'],
  })
  .use('sms-logs', restClient.service('sms-logs'), {
    methods: ['find', 'get', 'filters', 'summary'],
  })
  .use('email-logs', restClient.service('email-logs'), {
    methods: ['find', 'get', 'filters', 'summary'],
  })
  .use('email-message-templates', restClient.service('email-message-templates'), { methods: ['find', 'get', 'create', 'patch', 'remove'] })
  .use('email-mailings', restClient.service('email-mailings'), { methods: ['find', 'get', 'remove', 'getForDuplicate'] })
  .use('email-queue', restClient.service('email-queue'), {
    methods: ['create', 'find', 'get', 'filters', 'createBulk', 'getPreview', 'approveQueueItem', 'requeueItem'],
  })
  .use('invoices', restClient.service('invoices'), {
    methods: [
      'find',
      'get',
      'create',
      'patch',
      'remove',
      'removeAttachment',
      'mergeInvoices',
      'startGenerateInvoices',
      'createInvoicePDF',
      'createCredit',
      'createCreditInvoices',
      'email',
      'filters',
    ],
  })
  .use('sepa', restClient.service('sepa'), {
    methods: ['find', 'get', 'patch', 'remove', 'generateBatchFile', 'removeInvoicesFromBatch'],
  })
  .use('subscriptions', restClient.service('subscriptions'), {
    methods: [
      'find',
      'get',
      'create',
      'patch',
      'remove',
      'filters',
      'syncPrices',
      'contractTakeOver',
      'revert',
      'cancelSubscription',
      'reactivateSubscription',
    ],
  })
  .use('product-groups', restClient.service('product-groups'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('purchase-orders', restClient.service('purchase-orders'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('stock-locations', restClient.service('stock-locations'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('assets', restClient.service('assets'), {
    methods: ['find', 'get', 'patch', 'filters'],
  })
  .use('stock', restClient.service('stock'), {
    methods: ['find', 'get', 'patch', 'filters', 'summary'],
  })
  .use('stock-movements', restClient.service('stock-movements'), {
    methods: ['find', 'get', 'filters', 'book', 'reserve', 'pending'],
  })
  .use('products', restClient.service('products'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'uploadPriceList', 'filters'],
  })
  .use('contacts', restClient.service('contacts'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('domain-ns-groups', restClient.service('domain-ns-groups'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('domains', restClient.service('domains'), {
    methods: [
      'find',
      'get',
      'create',
      'patch',
      'remove',
      'check',
      'getEPPCode',
      'sendEPPCode',
      'forceRemove',
      'restore',
      'lock',
      'removeDNSSEC',
      'createDNSSEC',
      'createFull',
      'filters',
    ],
  })
  .use('webhooks', restClient.service('webhooks'), { methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'listServices', 'rotateSecret'] })
  .use('webhook-queue', restClient.service('webhook-queue'), { methods: ['find', 'get', 'filters', 'retry'] })
  .use('webhook-logs', restClient.service('webhook-logs'), { methods: ['find', 'get', 'filters'] })
  .use('dmarc', restClient.service('dmarc'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'defaults'],
  })
  .use('dmarc-aggregate-reports', restClient.service('dmarc-aggregate-reports'), {
    methods: ['find', 'get', 'remove', 'filters', 'summary', 'processMailbox'],
  })
  .use('dmarc-failure-reports', restClient.service('dmarc-failure-reports'), {
    methods: ['find', 'get', 'remove', 'filters', 'summary', 'processMailbox'],
  })
  .use('projects', restClient.service('projects'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('project-tasks', restClient.service('project-tasks'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('project-task-types', restClient.service('project-task-types'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('webhooks/health-status', restClient.service('webhooks/health-status'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'patchBulk', 'timeline'],
  })
  .use('project-task-time-tracking', restClient.service('project-task-time-tracking'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'generateInvoice', 'filters'],
  })
  .use('vpn-firewalls', restClient.service('vpn-firewalls'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'analyzeRuleImpact', 'analyzeRules', 'getVerificationOverview', 'verifyRule', 'revokeRule'],
  })
  .use('vpn-firewall-verification-logs', restClient.service('vpn-firewall-verification-logs'), {
    methods: ['find', 'get'],
  })
  .use('vpn-certificates', restClient.service('vpn-certificates'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'download', 'filters', 'buildAccessGraph', 'getRoutes'],
  })
  .use('settings', restClient.service('settings'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'orderSettings', 'filters', 'findSettings', 'resolveSettings', 'saveSettings', 'removeSettings'],
  })
  .use('feedback-answers', restClient.service('feedback-answers'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'summary', 'filters'],
  })
  .use('feedback-questions', restClient.service('feedback-questions'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('sharingcenter-groups', restClient.service('sharingcenter-groups'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'findUsers'],
  })
  .use('sharingcenter-calendars', restClient.service('sharingcenter-calendars'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'findUsers', 'findGroups'],
  })
  .use('sharingcenter-calendar-settings', restClient.service('sharingcenter-calendar-settings'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'updateOrder'],
  })
  .use('sharingcenter-appointments', restClient.service('sharingcenter-appointments'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'findUsers', 'validateRSVP', 'setRSVP', 'getMeetingByIdentifier'],
  })
  .use('sharingcenter-meeting-logs', restClient.service('sharingcenter-meeting-logs'), {
    methods: ['find', 'get', 'summary', 'filters', 'generateMinutes', 'generateTranscripts'],
  })
  .use('sharingcenter-meeting-files', restClient.service('sharingcenter-meeting-files'), {
    methods: ['find', 'get', 'remove'],
  })
  .use('sharingcenter-ai-queue', restClient.service('sharingcenter-ai-queue'), {
    methods: ['find', 'get', 'remove', 'summary', 'requeue'],
  })
  .use('sharingcenter-contacts', restClient.service('sharingcenter-contacts'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'findUsers', 'findGroups'],
  })
  .use('sharingcenter-profiles', restClient.service('sharingcenter-profiles'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters', 'findUsers', 'findGroups'],
  })
  .use('customer-sip-accounts', restClient.service('customer-sip-accounts'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('customer-phone-numbers', restClient.service('customer-phone-numbers'), {
    methods: ['find', 'get', 'create', 'patch', 'remove', 'filters'],
  })
  .use('sharingcenter-asterisk-config/status', restClient.service('sharingcenter-asterisk-config/status'), {
    methods: ['find'],
  })

export const api = createPiniaClient(feathersClient, {
  pinia,
  idField: '_id',
  whitelist: ['$in'],
  paramsForServer: [],
  customizeStore: (store) => {
    store.defaultLimit = undefined
    store.defaultSkip = undefined
  },
  services: {
    'sharingcenter-appointments': {
      idField: 'occurrenceId',
    },
  },
})

api.hooks({ before: { all: [skipWebhookStore], find: [skipListStore] } })
